import { FormGroup, Radio, RadioItem, Spinner, Text, TextArea } from '@agro-club/agroclub-shared'
import { useTableData } from 'modules/domain/common/hooks'
import { FormikFieldsType } from 'modules/domain/types'
import { ProfileType } from 'modules/domain/user/types'
import { endpoints } from 'modules/endpoints'
import { FC, ReactNode, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { FormPhoneInput } from 'views/components/form/FormPhoneInput'
import { FormRadio } from 'views/components/form/FormRadio'
import FormSelectShared from 'views/components/FormSelectShared'
import { QuantityInput } from 'views/components/QuantityInput/QuantityInput'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import { AnalysisTypes, Lab, LaboratoryCodes } from 'views/pages/Samples/types'
import { FormItemWrapper } from 'views/styled/common'

const FlexGapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`

type Props = {
  formik: FormikFieldsType
  children?: ReactNode
}

const params = { ...defaultListRequestParams }
export const CommonFormFields: FC<Props> = ({ formik, children }) => {
  const { t } = useTranslation(['deal', 'common', 'samples'])
  const { data: labs } = useTableData<Lab>(endpoints.labs(), params, true)

  const onLabChange = useCallback(
    (lab_code: string) => {
      if (!labs) return
      const selected = labs.find((l) => l.code === lab_code)
      if (!selected || !selected.senior_lab_assistant) return
      formik.setFieldValue('lab_assistant', selected.senior_lab_assistant.id.toString())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labs],
  )

  useEffect(() => {
    onLabChange(formik.values.laboratory)
  }, [formik.values.laboratory, onLabChange])

  const labsOptions = labs
    ?.map((l) => <RadioItem key={l.code} value={l.code} label={t(`samples:laboratories.${l.code}`)} />)
    .reverse()

  return (
    <>
      <FormSelectShared
        label={t('accordion.sample.seniorRegional')}
        formik={formik}
        endpoint={`${endpoints.coordinatorsShort()}?coordinator_type=senior_regional`}
        fieldName="senior_regional"
        getLabel={(dto) => dto.full_name}
        placeholder=""
        searchField="search"
      />
      {children}
      <QuantityInput formik={formik} fieldName="quantity" label={t('common:volume')} />
      <FormSelectShared
        label={t('common:product')}
        formik={formik}
        endpoint={endpoints.products()}
        fieldName="product"
        placeholder=""
        getLabel={(dto) => dto.title}
        simpleApiFormat
        showSearch={false}
      />
      <FormSelectShared
        label={t('seller')}
        formik={formik}
        onChange={() => formik.setFieldValue('seller_address', null)}
        endpoint={endpoints.activeUsersByTypes([ProfileType.seller, ProfileType.broker])}
        searchField="search"
        fieldName="seller_user"
        placeholder=""
        getLabel={(dto) => dto.profile?.full_name || t('common:withoutName')}
      />
      <FormPhoneInput formik={formik} required={false} />
      <FormSelectShared
        label={t('accordion.sample.address')}
        formik={formik}
        endpoint={formik.values.seller_user ? endpoints.userAddress(formik.values.seller_user) : ''}
        fieldName="seller_address"
        placeholder=""
        getLabel={(dto) => dto.address}
        simpleApiFormat
        disabled={!formik.values.seller_user}
        showSearch={false}
      />
      <FormGroup label={t('accordion.sample.laboratory')}>
        <FlexGapWrapper>
          <FormItemWrapper>
            {labsOptions ? (
              <Radio
                onChange={(lab_code) => formik.setFieldValue('laboratory', lab_code)}
                value={formik.values.laboratory}
              >
                {labsOptions}
              </Radio>
            ) : (
              <Spinner size="small" />
            )}
          </FormItemWrapper>
          {formik.values.laboratory === LaboratoryCodes.other && (
            <>
              <TextArea
                {...formik.getFieldProps('lab_comment')}
                invalid={!!formik.errors.lab_comment}
                height={80}
                borderRadius="form"
              />
              <Text typography="bodySmall" color="secondary">
                {t('accordion.sample.laboratoryComment')}
              </Text>
            </>
          )}
        </FlexGapWrapper>
      </FormGroup>
      <FormRadio label={t('samples:analysis_type')} formik={formik} field="analysis_type">
        <RadioItem value={AnalysisTypes.gost} label={t('samples:analysisTypes.gost')} />
        <RadioItem value={AnalysisTypes.iso} label={t('samples:analysisTypes.iso')} />
        <RadioItem value={AnalysisTypes.en} label={t('samples:analysisTypes.en')} />
        <RadioItem value={AnalysisTypes.isiri} label={t('samples:analysisTypes.isiri')} />
      </FormRadio>
      <FormSelectShared
        label={t('accordion.sample.labAssistant')}
        formik={formik}
        endpoint={`${endpoints.users()}?coordinator_type=lab_assistant`}
        fieldName="lab_assistant"
        placeholder=""
        getLabel={(dto) => dto.profile.full_name}
        searchField="search"
        onOptionsChanged={(dto) =>
          dto.length && !formik.values.lab_assistant && formik.setFieldValue('lab_assistant', dto[0].value)
        }
      />
      <FormGroup error={formik.errors.comment} label={t('common:comment')}>
        <TextArea
          {...formik.getFieldProps('comment')}
          borderRadius="form"
          invalid={!!formik.errors.comment}
          height={112}
          limit={255}
        />
      </FormGroup>
    </>
  )
}
